import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Message } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Md5 } from 'ts-md5';
import { AuthService } from '../../../core/auth/auth.service';
import { CommonValidationService } from '../../../core/common-validation.service';
import { ConfigService } from '../../../core/config.service';
import { DisplayMessage } from '../../../core/messages/display-message';
import { Severity } from '../../../shared/enums/severity';

@Component({
  selector: 'hb-password-reset',
  templateUrl: './password-reset.component.html',
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  @Input() showContinue: boolean = true;
  @Input() showCancel: boolean = false;
  @Output() public resetSuccessful = new EventEmitter<boolean>();

  private unsubscribe: Subject<boolean> = new Subject();
  private returnUrl: string;
  private currentPassword: string;
  private newPassword: string;
  private username: string;

  statusMessage: string;

  messages: Message[] = [];

  // This is the correct way to specify Validators
  /* eslint-disable @typescript-eslint/unbound-method */
  currentPasswordFormControl = new FormControl('', [Validators.required]);
  passwordFormControl = new FormControl('', [Validators.required]);
  verifyPasswordFormControl = new FormControl('', [Validators.required]);
  /* eslint-enable @typescript-eslint/unbound-method */

  currentPasswordError: boolean;
  resetPasswordError: boolean;
  resetPasswordErrorMessage: string;
  resetSuccess: boolean;

  constructor(
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute,
    private validationService: CommonValidationService,
    private configService: ConfigService,
  ) { }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    this.username = this.authService.loggedInUser.getValue();
    this.resetPasswordErrorMessage = '';
  }

  resetPassword() {
    if (!this.validatePasswords()) {
      return;
    }

    this.performPasswordReset();
  }

  cancel() {
    this.authService.logout();
  }

  private performPasswordReset() {
    this.authService
      .resetPassword(this.currentPassword, this.newPassword).pipe(
        takeUntil(this.unsubscribe))
      .subscribe(
        udpatedUser => {
          if (udpatedUser && !udpatedUser.passwordResetRequired) {
            this.configService.setResetRequired(false);
            this.resetSuccess = true;
            this.resetSuccessful.emit(true);
            this.statusMessage = 'Your password has been sucessfully reset.';
          }
        },
        error => {
          if (error && error.messages) {
            this.resetSuccess = false;
            error.messages.forEach(element => {
              if (element.text === 'Invalid Password') {
                this.currentPasswordError = true;
                this.resetPasswordErrorMessage = 'Invalid Password';
                return;
              }
              this.messages.push(new DisplayMessage('', element.text, Severity.Error));
            });
          }
        },
      );
  }

  validatePasswords(): boolean {
    this.username = this.authService.loggedInUser.getValue();
    this.resetPasswordError = false;
    this.resetPasswordErrorMessage = '';

    this.currentPasswordError = !this.currentPasswordFormControl.value;

    if (!this.currentPasswordFormControl.value) {
      this.resetPasswordErrorMessage = 'Enter your current password.';
      return false;
    }

    // Check password is valid
    if (!this.passwordFormControl.value || !this.validationService.validatePassword(this.passwordFormControl.value, this.username)) {
      this.resetPasswordError = true;
      this.resetPasswordErrorMessage = this.validationService.failedPasswordVailidationMessage().trim();
      return false;
    }

    // Check that the verifyPassword matches the password if the password has been touched
    if (!this.verifyPasswordFormControl.value || this.passwordFormControl.value !== this.verifyPasswordFormControl.value) {
      this.resetPasswordError = true;
      this.resetPasswordErrorMessage = 'Passwords do not match.';
      return false;
    }

    this.currentPassword = Md5.hashStr(this.currentPasswordFormControl.value.trim() + this.username.toLowerCase().trim()).toString();
    this.newPassword = Md5.hashStr(this.passwordFormControl.value.trim() + this.username.toLowerCase().trim()).toString();

    return true;
  }

  continue() {
    this.authService.loggedIn.next(true);
    this.authService.passwordAlreadyResetted();
    this.router.navigateByUrl(this.returnUrl);
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }
}
